/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {animated, useTransition} from 'react-spring';

import debounce from 'lodash/debounce';
import noop from 'lodash/noop';

import {useEvent} from '@edna/hooks';

import {useTheme} from '../styles';
import {EPlacementX, EPlacementY, TPlacementX, TPlacementY} from '../types';
import {easing} from '../utils';
import * as S from './style';
import {TCloseWhen, TCloseWhenHandler, THead, TOverlay, TTrigger} from './types';
import {closeWhenMap, makeValidElement} from './utils';

export * from './types';
export * from './utils';

const useMouseOver = (callback: (state: boolean) => void, hoverDelay?: number) => {
  const theme = useTheme();

  const leaveTimeout = hoverDelay ?? theme.animation.duration / 2;

  const handleMouseLeave = React.useMemo(
    () => debounce(() => callback(false), leaveTimeout),
    [callback, leaveTimeout],
  );

  const handleMouseEnter = React.useCallback(() => {
    handleMouseLeave.cancel();
    callback(true);
  }, [callback, handleMouseLeave]);

  return [handleMouseEnter, handleMouseLeave];
};

const useCloseHandler = (
  active: boolean,
  closeWhen: TProps['closeWhen'],
  onClose: TEmptyFunction,
) => {
  const headRef = React.useRef<HTMLDivElement>(null);
  const overlayRef = React.useRef<HTMLDivElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const handleClick = React.useCallback(
    (event: MouseEvent) => {
      const target = event.target as Node | null;
      const headNode = headRef.current;
      const overlayNode = overlayRef.current;
      const dropdownNode = dropdownRef.current;

      if (
        target === null ||
        headNode === null ||
        overlayNode === null ||
        dropdownNode === null ||
        (closeWhen !== 'clickOutsideDropdown' && headNode.contains(target)) ||
        // если клик на удаленный элемент, то не закрываем
        !document.documentElement.contains(target)
      ) {
        return;
      }

      const needClose = () => {
        if (typeof closeWhen === 'function') {
          return closeWhen(target, headNode, overlayNode, dropdownNode);
        }

        if (typeof closeWhen === 'string') {
          return closeWhenMap[closeWhen](target, headNode, overlayNode, dropdownNode);
        }

        return false;
      };

      if (needClose()) {
        onClose();
      }
    },
    [closeWhen, onClose],
  );

  useEvent('click', active ? handleClick : noop);

  return [headRef, overlayRef, dropdownRef];
};

const usePlacementX = (
  active: boolean,
  ref: React.RefObject<HTMLElement>,
  initial: TPlacementX,
) => {
  const [placementX, setPlacementX] = React.useState<TPlacementX>(initial);

  const updatePlacementX = React.useCallback(() => {
    if (!active || ref.current === null) {
      return;
    }

    const headRect = ref.current.getBoundingClientRect();
    const headCenter = headRect.x + headRect.width / 2;
    const documentCenter = document.documentElement.offsetWidth / 2;

    setPlacementX(headCenter > documentCenter ? 'right' : 'left');
  }, [active, ref]);

  React.useEffect(() => {
    if (!active) {
      return noop;
    }

    updatePlacementX();

    const mutationObserver = new MutationObserver(updatePlacementX);

    mutationObserver.observe(ref.current as Node, {
      childList: true,
      subtree: true,
    });

    return () => {
      mutationObserver.disconnect();
    };
  }, [active, ref, updatePlacementX]);

  return placementX;
};

const useOverlayTransitions = (placementY: TPlacementY, isOpen: boolean, duration: number) => {
  return useTransition<boolean, React.CSSProperties>(isOpen, {
    from: {opacity: 0, marginTop: placementY === 'bottom' ? '-10px' : '10px'},
    enter: {opacity: 1, marginTop: '0px'},
    leave: {opacity: 0},
    config: {
      duration,
      easing,
    },
  });
};

export type TProps = {
  className?: string;
  head?: THead;
  headComponent?: TAnyComponent;
  overlay?: TOverlay;
  overlayComponent?: TAnyComponent;
  placementX?: TPlacementX;
  placementY?: TPlacementY;
  offsetY?: number;
  offsetX?: number;
  trigger?: TTrigger;
  closeWhen?: TCloseWhen | TCloseWhenHandler;
  onFocus?: React.FocusEventHandler<HTMLElement>;
  onBlur?: React.FocusEventHandler<HTMLElement>;
  disabled?: boolean;
  readOnly?: boolean;
  overlayColor?: string;
  hasOverlayArrow?: boolean;
  hasVerticalPaddings?: boolean;
  scrollTo?: number;
  onOpen?: TEmptyFunction;
  onClose?: TEmptyFunction;
  hoverDelay?: number;
  disableHoverOverlay?: boolean;
  overlayClassName?: string;
};

const SMALL_HEAD_WIDTH = 30;

export const Dropdown = React.memo<TProps>(
  ({
    className,
    head,
    headComponent: Head = S.Head,
    overlay,
    overlayComponent = S.Overlay,
    placementX: propPlacementX,
    placementY = EPlacementY.bottom,
    offsetY = 10,
    offsetX: propOffset = 0,
    trigger = 'click',
    closeWhen = 'clickAny',
    onFocus = noop,
    onBlur = noop,
    disabled,
    readOnly,
    overlayColor,
    hasOverlayArrow,
    hasVerticalPaddings,
    scrollTo,
    onOpen = noop,
    onClose = noop,
    hoverDelay,
    disableHoverOverlay,
    overlayClassName,
  }) => {
    const {animation} = useTheme();
    const [isOpen, setIsOpen] = React.useState(false);
    const delay = React.useMemo(() => {
      if (trigger === 'hover') {
        return hoverDelay ?? animation.duration / 2;
      }

      return animation.duration;
    }, [animation.duration, hoverDelay, trigger]);

    const toggleIsOpen = React.useCallback(() => setIsOpen((prevState) => !prevState), []);
    const handleClose = React.useCallback(() => {
      setIsOpen(false);

      if (closeWhen === 'clickOutsideDropdown') {
        onClose();
      }
    }, [closeWhen, onClose]);

    const [handleMouseEnter, handleMouseLeave] = useMouseOver(setIsOpen, delay);

    const [headRef, overlayRef, dropdownRef] = useCloseHandler(
      isOpen && trigger === 'click',
      closeWhen,
      handleClose,
    );
    const placementX = usePlacementX(
      isOpen && propPlacementX === undefined,
      headRef,
      propPlacementX ?? EPlacementX.left,
    );

    const headWidth = headRef?.current?.clientWidth;

    const offsetX = React.useMemo(() => {
      if (propOffset > 0 || headWidth === undefined) {
        return propOffset;
      }

      return headWidth < SMALL_HEAD_WIDTH ? -10 : 0;
    }, [headWidth, propOffset]);

    const Overlay = React.useMemo(() => animated(overlayComponent), [overlayComponent]);

    const overlayTransitions = useOverlayTransitions(placementY, isOpen, delay);

    const headHandlers = React.useMemo(() => {
      if (disabled || readOnly) {
        return {};
      }

      const handlers: Record<string, TAnyFunction> = {onFocus, onBlur};

      if (trigger === 'hover') {
        handlers.onMouseEnter = handleMouseEnter;
        handlers.onMouseLeave = handleMouseLeave;
      }

      if (trigger === 'click' && closeWhen === 'clickOutsideDropdown') {
        handlers.onClick = () => setIsOpen(true);

        return handlers;
      }

      if (trigger === 'click' && closeWhen !== 'clickOutsideDropdown') {
        handlers.onClick = toggleIsOpen;
      }

      return handlers;
    }, [
      closeWhen,
      disabled,
      readOnly,
      onFocus,
      onBlur,
      trigger,
      toggleIsOpen,
      handleMouseEnter,
      handleMouseLeave,
    ]);

    const overlayHandlers = React.useMemo(() => {
      const handlers: Record<string, TAnyFunction> = {};

      if (trigger === 'hover' && !disableHoverOverlay) {
        handlers.onMouseEnter = handleMouseEnter;
        handlers.onMouseLeave = handleMouseLeave;
      }

      return handlers;
    }, [trigger, handleMouseEnter, handleMouseLeave]);

    React.useEffect(() => {
      if (closeWhen === 'clickOutsideDropdown') {
        return;
      }

      if (isOpen) {
        onOpen();
      } else {
        onClose();
      }
    }, [isOpen, onClose, onOpen]);

    React.useEffect(() => {
      if (isOpen && scrollTo !== undefined && overlayRef.current) {
        overlayRef.current.scrollTop = scrollTo;
      }
    }, [isOpen, scrollTo, overlayRef]);

    return (
      <S.Dropdown className={className} ref={dropdownRef}>
        <Head {...headHandlers} ref={headRef} role="button" tabIndex={0} disabled={disabled}>
          {makeValidElement(head, {isOpen})}
        </Head>
        {overlayTransitions(
          (style, state) =>
            state && (
              <Overlay
                {...overlayHandlers}
                hasArrow={hasOverlayArrow}
                ref={overlayRef}
                backgroundColor={overlayColor}
                hasVerticalPaddings={hasVerticalPaddings}
                style={style}
                placementY={placementY}
                placementX={placementX}
                offsetY={offsetY}
                offsetX={offsetX}
                className={overlayClassName}
              >
                {makeValidElement(overlay, {close: handleClose})}
              </Overlay>
            ),
        )}
      </S.Dropdown>
    );
  },
);

export default Dropdown;

export const {Head, Overlay} = S;

/* eslint-enable react/jsx-props-no-spreading */
